import { closeCurrentModal } from 'weblite-web-modal-viewer'
import {
  addAdmin,
  changeAdminPermissions,
  decodeChannelAdminPermissions,
  decodeSubscriptionPermissions,
  getChannelManager,
  removeAdmins,
  setQuestionSupport,
} from '$logic/subscription'
import {
  CHANNEL_ADMIN_RIGHTS,
  HallPermissionsPage,
} from '$common/hall-permissions-page'
import { t } from '$i18n'
import { useAsyncFunction } from '$func/async-function.react-hook'
import { closeChannelAdminPermissions } from '$logic/router'
import { showSaveChangesLoadingModal } from '$comp/modals/loading-modal'
import { useClientUserPermissions } from '$logic/subscription/react-hook'
import { showPromoteToOwnerConfirmModal } from '$comp/modals/confirmation-dialog'
import { resetOpenedUserList } from '$logic/opened-user-list'
import { showOperationFailedSnackbar } from '$comp/snackbars'
import { asyncDelay } from '$func/delay'
import { ChannelManagerPermissions } from '$logic/subscription/question-support.action'

interface Props {
  userId: string
  hallId: string
  mode: 'DEFINE' | 'EDIT'
}

export function ChannelAdminRights({ userId, hallId, mode }: Props) {
  const useAsyncFunctionCallBack =
    mode === 'DEFINE'
      ? async () => ({
          ...(await decodeSubscriptionPermissions(hallId)),
          questionSupport: false,
        })
      : async () => {
          const manager = await getChannelManager(hallId, userId)
          if (!manager) return undefined
          return {
            ...(await decodeChannelAdminPermissions(
              hallId,
              manager.permissions,
            )),
            questionSupport: manager.role === 'QUESTION_SUPPORT',
          }
        }

  const { value: initialPermissions } =
    useAsyncFunction<ChannelManagerPermissions>(useAsyncFunctionCallBack, [
      hallId,
      userId,
    ])

  const clientUserPermissions = useClientUserPermissions(hallId)

  const isQuestionSupport = initialPermissions?.questionSupport

  const onComplete = async (permissions: ChannelManagerPermissions) => {
    showSaveChangesLoadingModal()
    if (mode === 'DEFINE') {
      analite('CHAT_ADD_ADMIN', { type: 'CHANNEL' })
      if (permissions.questionSupport) {
        await setQuestionSupport(hallId, userId, true)
      } else {
        await addAdmin(hallId, userId, permissions)
      }
    } else if (isQuestionSupport) {
      if (!permissions.questionSupport) {
        await setQuestionSupport(hallId, userId, false)
      }
    } else {
      await changeAdminPermissions(hallId, userId, permissions)
    }

    resetOpenedUserList()
    resetOpenedUserList('admins')

    await closeCurrentModal()
    await closeChannelAdminPermissions()
  }

  const onRemoveAdmin = async () => {
    showSaveChangesLoadingModal()

    const successful = isQuestionSupport
      ? await setQuestionSupport(hallId, userId, false)
      : await removeAdmins(hallId, [userId])
    if (!successful) {
      await closeCurrentModal()
      showOperationFailedSnackbar(() =>
        isQuestionSupport
          ? setQuestionSupport(hallId, userId, false)
          : removeAdmins(hallId, [userId]),
      )

      return
    }

    resetOpenedUserList()
    resetOpenedUserList('admins')

    // Delay closing page to ensure previous pages are re-rendered
    await asyncDelay(0)

    await closeCurrentModal()
    await closeChannelAdminPermissions()
  }

  const onPromoteToOwner = async () =>
    showPromoteToOwnerConfirmModal(hallId, userId)

  return (
    <HallPermissionsPage
      confirmDisabled={false}
      rights={CHANNEL_ADMIN_RIGHTS.map(right => ({
        ...right,
        isLocked:
          mode === 'EDIT' && isQuestionSupport
            ? right.id !== 'questionSupport'
            : mode === 'EDIT' && right.id === 'questionSupport'
              ? true
              : right.id === 'questionSupport'
                ? !clientUserPermissions?.addNewAdmins
                : !clientUserPermissions?.[right.id],
      }))}
      actionButtonProps={
        mode !== 'DEFINE'
          ? {
              text: t(
                'hallPermissionsPage.channelAdminRights.actionButtonText',
              ),
              color: 'error',
            }
          : undefined
      }
      secondaryActionButtonProps={{
        text: t('hallPermissionsPage.channelAdminRights.secondaryButtonText'),
        color: 'secondary',
      }}
      pageTitle={t('hallPermissionsPage.channelAdminRights.title')}
      subTitle={t('hallPermissionsPage.channelAdminRights.subTitle')}
      userId={userId}
      initialPermissions={initialPermissions}
      onBack={closeChannelAdminPermissions}
      onComplete={onComplete}
      onActionButtonClick={onRemoveAdmin}
      onSecondaryActionButton={
        clientUserPermissions?.isOwner && !isQuestionSupport
          ? onPromoteToOwner
          : undefined
      }
    />
  )
}
